import { useEffect } from "react";
import { api } from "@/lib/api";

/**
 * Runs once at app mount. Ensures:
 *   1. `localStorage.f2n_last_center` is populated (from prior sessions,
 *      the user's saved profile city, or a fresh browser GPS ping).
 *   2. `document.documentElement.lang` reflects the persisted lang so any
 *      third-party widget that reads it (voice-assistants, screen readers,
 *      Grab Maps localisation) picks up the right locale.
 *
 * Renders nothing. Failure is silent — the app must never block on this.
 */
export default function LocationBootstrap() {
  useEffect(() => {
    // Language HTML attr sync (I18nContext also sets this, but on a delayed
    // effect after language-strings load — we set it earlier here so above-
    // the-fold widgets don't flash the wrong locale).
    try {
      const lang = localStorage.getItem("f2n_lang");
      if (lang) document.documentElement.setAttribute("lang", lang);
    } catch { /* ignore */ }

    // Skip GPS if we already have a persisted centre (respect user's choice).
    let cached = null;
    try { cached = localStorage.getItem("f2n_last_center"); } catch { /* ignore */ }
    if (cached) return;

    // 1. Try the user's saved profile location first — no permission prompt.
    (async () => {
      try {
        const r = await api.get("/me/location");
        const p = r.data;
        if (p && Number.isFinite(p.lat) && Number.isFinite(p.lng)) {
          localStorage.setItem("f2n_last_center", JSON.stringify([p.lat, p.lng]));
          if (p.city) localStorage.setItem("f2n_last_city", p.city);
          return;
        }
      } catch { /* anonymous or endpoint missing — fall through */ }

      // 2. Browser GPS (asks permission, only in secure contexts).
      if (typeof navigator === "undefined" || !navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          try {
            localStorage.setItem(
              "f2n_last_center",
              JSON.stringify([pos.coords.latitude, pos.coords.longitude]),
            );
            window.dispatchEvent(new CustomEvent("f2n:location-updated"));
          } catch { /* ignore */ }
        },
        () => { /* denied — no-op, downstream pages default to Mumbai */ },
        { enableHighAccuracy: false, timeout: 6000, maximumAge: 15 * 60 * 1000 },
      );
    })();
  }, []);

  return null;
}
